<!--
  File name: ProfileForm.vue
  Program Description:
    - This component displays the profile form for the system.
    - When accessed, it accepts, displays, or handles updates the profile
    details of the user
-->

<template>
  <v-col
    class="pa-0"
  >
    <v-card
      tile
      :class="styles"
    >
      <v-row
        v-if="$data.$meta.readOnly"
        class="pb-3 text-h5 primary--text"
      >
        <span
          v-if="operation !== 'create'"
          style="margin:auto`"
          class="font-weight-bold"
        >General Information</span>
        <v-spacer />
        <div
          :style="isActive ? 'width: 60%' : 'width: 30%; margin-right: 80px;'"
          :class="!operation ? 'd-flex justify-end mr-5':'d-flex align-center justify-space-around'"
        >
          <v-switch
            v-if="operation"
            id="status-button"
            v-model="isActive"
            color="success"
            style="font-size:14px;"
            :loading="$data.$meta.isLoading"
            :disabled="$data.$meta.isLoading"
            dense
            @click="reasonDialog"
          >
            <template slot="label">
              <span class="black--text font-weight-medium">
                {{ isActive ? 'Disable' : 'Enable' }}
              </span>
            </template>
          </v-switch>
          <v-btn
            v-if="operation"
            icon
            color="red"
            label="delete"
            style="font-size:16px; letter-spacing: normal;"
            large
            :loading="$data.$meta.isLoading"
            @click="deleteDialog(userId)"
          >
            <v-icon>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-btn
            v-if="$data.$meta.isLoading"
            style="background-color:#162b54; color: #FFF; "
            class="font-weight-regular rounded-lg editBtn"
            :loading="$data.$meta.isLoading"
            type="button"
          />
          <v-btn
            v-if="$data.$meta.readOnly && showLabel('canUpdateOwnProfile') && isActive"
            id="edit-button"
            style="background-color:#162b54; color: #FFF; "
            class="font-weight-regular rounded-lg editBtn"
            type="button"
            :loading="$data.$meta.isLoading"
            @click="$data.$meta.readOnly = false; updateCrumb()"
          >
            <v-icon
              class="pr-2"
              style="font-size: 18px"
            >
              mdi-pencil
            </v-icon>
            {{ getLabel('canUpdateOwnProfile', 'Edit Profile') }}
          </v-btn>
        </div>
      </v-row>
      <v-row
        v-if="$data.$meta.readOnly"
        class="pt-8"
      >
        <v-col
          class="pa-0"
        >
          <p class="label">
            First Name
          </p>
          <v-text-field
            v-model="firstName"
            class="black--text "
            style="max-width: 95%"
            solo
            placeholder="First Name"
            flat
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('firstName','First Name')"
            @input="$v.firstName.$touch()"
            @blur="$v.firstName.$touch()"
          />
        </v-col>
        <v-col
          v-if="lastName.length < 30 && firstName.length < 30"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Last Name
          </p>
          <v-text-field
            v-model="lastName"
            class="black--text"
            style="max-width: 95%"
            solo
            flat
            placeholder="Last Name"
            dense
            :outlined="!$data.$meta.readOnly"
            :error-messages="genericError('lastName', 'Last Name')"
            :disabled="$data.$meta.readOnly"
            @input="$v.lastName.$touch()"
            @blur="$v.lastName.$touch()"
          />
        </v-col>
      </v-row>
      <v-row
        v-else
        class="pt-8"
      >
        <v-col
          cols="6"
          class="pa-0"
        >
          <v-text-field
            id="firstname-field"
            v-model.trim="firstName"
            label="First Name"
            class="black--text "
            style="max-width: 95%"
            placeholder="First Name"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('firstName','First Name')"
            @input="$v.firstName.$touch()"
            @blur="$v.firstName.$touch()"
          />
        </v-col>
        <v-col
          cols="6"
          class="pa-0"
        >
          <v-text-field
            id="lastname-field"
            v-model.trim="lastName"
            label="Last Name"
            class="black--text "
            style="max-width: 95%"
            placeholder="Last Name"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('lastName','Last Name')"
            @input="$v.lastName.$touch()"
            @blur="$v.lastName.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="($data.$meta.readOnly && lastName.length > 30) || $data.$meta.readOnly && firstName.length > 30 ">
        <v-col
          class="pa-0"
        >
          <p class="label">
            Last Name
          </p>
          <v-text-field
            v-model="lastName"
            class="black--text"
            style="max-width: 95%"
            solo
            flat
            placeholder="Last Name"
            dense
            :outlined="!$data.$meta.readOnly"
            :error-messages="genericError('lastName', 'Last Name')"
            :disabled="$data.$meta.readOnly"
            @input="$v.lastName.$touch()"
            @blur="$v.lastName.$touch()"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="$data.$meta.readOnly"
      >
        <v-col
          :cols="email.length >= 30 ? '12' : '6'"
          class="pa-0"
        >
          <p class="label">
            Official Email
          </p>
          <v-text-field
            v-model.trim="email"
            solo
            flat
            dense
            type="email"
            placeholder="Email"
            :outlined="!$data.$meta.readOnly && editable && isAdmin"
            :disabled="$data.$meta.readOnly || !editable || !isAdmin"
            :error-messages="genericError('email', 'Email')"
            @input="$v.email.$touch()"
            @blur="$v.email.$touch()"
          />
        </v-col>
        <v-col
          v-if="email.length < 30"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Contact Number
          </p>
          <v-text-field
            v-model.trim="contactNumber"
            class="black--text"
            solo
            flat
            placeholder="Contact number"
            type="text"
            dense
            :error-messages="genericError('contactNumber', 'Contact Number')"
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            @input="$v.contactNumber.$touch()"
            @blur="$v.contactNumber.$touch()"
          />
        </v-col>
      </v-row>
      <v-row
        v-else
        class="pt-4"
      >
        <v-col
          class="pa-0"
          cols="8"
        >
          <v-text-field
            id="email-field"
            v-model.trim="email"
            style="max-width: 95%;"
            label="Official Email"
            dense
            type="email"
            placeholder="Email"
            :outlined="operation === 'create' ? true : false"
            :disabled="!editable && operation !== 'create'"
            :error-messages="genericError('email', 'Email')"
            @input="$v.email.$touch()"
            @blur="$v.email.$touch()"
          />
        </v-col>
        <v-col
          cols="4"
          class="pa-0"
        >
          <v-text-field
            id="contactnumber-field"
            v-model.trim="contactNumber"
            label="Contact Number"
            class="black--text"
            style="max-width: 95%"
            type="text"
            dense
            :error-messages="genericError('contactNumber', 'Contact Number')"
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            @input="$v.contactNumber.$touch()"
            @blur="$v.contactNumber.$touch()"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="$data.$meta.readOnly"
      >
        <v-col
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Sex
          </p>
          <v-text-field
            v-model="sex"
            class="black--text"
            solo
            flat
            placeholder="Sex"
            dense
            disabled
          />
        </v-col>
        <v-col
          v-if="email.length >= 30"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Contact Number
          </p>
          <v-text-field
            v-model.trim="contactNumber"
            class="black--text"
            solo
            flat
            placeholder="Contact number"
            type="text"
            dense
            :error-messages="genericError('contactNumber', 'Contact Number')"
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            @input="$v.contactNumber.$touch()"
            @blur="$v.contactNumber.$touch()"
          />
        </v-col>
      </v-row>
      <v-row
        v-else
      >
        <v-col
          cols="4"
          class="px-0"
        >
          <VAutocomplete
            id="user-sex-field"
            v-model="sex"
            label="Sex"
            text-field-class="black--text font-weight-bold"
            dense
            is-required
            :items="$store.state.defaults.sex"
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('sex', 'Sex')"
            @input="$v.sex.$touch()"
            @blur="$v.sex.$touch()"
          />
        </v-col>
        <v-col
          v-if="!isPic"
          cols="8"
        >
          <VAutocomplete
            id="role-field"
            v-model="userRole"
            label="Role"
            :items="$store.state.defaults ? $store.state.defaults.userRole : []"
            class="black--text"
            item-text="name"
            item-value="value"
            :append-icon="operation === undefined ? '' : 'arrow_drop_down'"
            multiple
            chips
            deletable-chips
            :query="userRoleQuery"
            dense
            :outlined="operation === undefined ? false : true"
            :disabled="!operation"
            :error-messages="genericError('userRole', 'User Role')"
            @input="$v.userRole.$touch()"
            @blur="$v.userRole.$touch()"
          />
        </v-col>
        <v-col
          v-else
          cols="8"
        >
          <v-text-field
            id="organization-field"
            v-model.trim="organization"
            class="black--text"
            label="Name of Organization"
            type="text"
            dense
            :outlined="isAdmin"
            :disabled="!isAdmin"
            :error-messages="genericError('organization','Organization')"
            @input="$v.organization.$touch()"
            @blur="$v.organization.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="!isPic">
        <v-col
          v-if="$data.$meta.readOnly"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Role
          </p>
          <VAutocomplete
            v-model="userRole"
            :items="$store.state.defaults ? $store.state.defaults.userRole : []"
            class="black--text"
            item-text="name"
            item-value="value"
            multiple
            chips
            deletable-chips
            :query="userRoleQuery"
            dense
            disabled
            append-icon=""
            rounded
          />
        </v-col>
        <v-col
          v-else
          cols="12"
          class="pa-0 "
        >
          <v-text-field
            id="division-field"
            v-model.trim="division"
            class="black--text"
            label="Division"
            placeholder="Division"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('division','Division')"
            @input="$v.division.$touch()"
            @blur="$v.division.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="isPic">
        <v-col
          v-if="$data.$meta.readOnly"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Name of Organization
          </p>
          <v-text-field
            v-model.trim="organization"
            class="black--text"
            solo
            flat
            placeholder="Organization"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
          />
        </v-col>
      </v-row>
      <v-row v-if="isPic">
        <v-col
          v-if="$data.$meta.readOnly"
          cols="12"
          class="pa-0"
        >
          <p class="label">
            Sector
          </p>
          <LookUpSelect
            v-model="sector"
            class="look-up-select"
            name="sector"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            :clearable="false"
            outlined
            disabled
            readonly
            text-wrap
            hide-details
            append-icon=""
            item-text="value"
            item-value="_id"
          />
        </v-col>
        <v-col
          v-show="!$data.$meta.readOnly"
          cols="6"
          class="pa-0 pt-4"
        >
          <LookUpSelect
            v-model="sector"
            class="look-up-select"
            label="Sector"
            name="sector"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            :query="{'type': 'sector'}"
            :allow-fetch="!$data.$meta.readOnly"
            :can-edit="isAdmin"
            :readonly="!isAdmin"
            :clearable="false"
            :is-dense="true"
            :outlined="isAdmin"
            :append-icon="isAdmin ? 'arrow_drop_down' : ''"
            item-text="value"
            item-value="_id"
            text-wrap
            :error-messages="genericError('sector', 'Sector')"
            @input="$v.sector.$touch(), subsector = undefined"
            @blur="$v.sector.$touch()"
          />
        </v-col>
        <v-col
          v-show="!$data.$meta.readOnly"
          cols="6"
          class="pa-0 pt-4 pl-2"
        >
          <LookUpSelect
            v-model.trim="subsector"
            class="look-up-select"
            label="Sub Sector"
            name="sector"
            :query="{'jurisdiction': sector}"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            :allow-fetch="sector"
            :can-edit="isAdmin"
            :clearable="false"
            :is-dense="true"
            :outlined="isAdmin"
            item-text="value"
            item-value="_id"
            :append-icon="isAdmin ? 'arrow_drop_down' : ''"
          />
        </v-col>
      </v-row>
      <v-row v-if="isPic">
        <v-col
          v-if="$data.$meta.readOnly"
          cols="12"
          class="pa-0"
        >
          <p class="label">
            Sub Sector
          </p>
          <LookUpSelect
            v-model.trim="subsector"
            class="look-up-select"
            name="sector"
            :query="{'jurisdiction': sector}"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6 look-up-disabled"
            :allow-fetch="sector"
            :clearable="false"
            :outlined="true"
            :disabled="false"
            append-icon=""
            item-text="value"
            item-value="_id"
          />
        </v-col>
        <v-col
          v-if="!$data.$meta.readOnly && !$data.$meta.isLoading"
          cols="12"
          class="pa-0 d-flex justify-end align-center"
        >
          <slot name="action" />
        </v-col>
        <v-col
          v-if="npcrsRegistration.valid"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            NPCRS Registration
          </p>
          <p class="label">
            DPO Name: <span class="black--text">{{ npcrsRegistration.dpoName }}</span>
          </p>
          <p class="label">
            DPO Email: <span class="black--text">{{ npcrsRegistration.officialDpoEmail }}</span>
          </p>
          <p class="label">
            Validity Period: <span class="black--text">{{ npcrsRegistration.validityPeriod }}</span>
          </p>
        </v-col>
        <v-col
          v-if="npcrsRegistration.picName"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Organization
          </p>
          <p class="label">
            PIC Name: <span class="black--text">{{ npcrsRegistration.picName }}</span>
          </p>
          <p class="label">
            PIC Email: <span class="black--text">{{ npcrsRegistration.picEmail }}</span>
          </p>
          <p class="label">
            Company Address: <span class="black--text">{{ npcrsRegistration.companyAddress }}</span>
          </p>
        </v-col>
      </v-row>
      <v-row v-if="!isPic">
        <v-col
          v-if="$data.$meta.readOnly"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Division
          </p>
          <v-text-field
            v-model.trim="division"
            class="black--text"
            solo
            flat
            placeholder="Division"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('division','Division')"
            @input="$v.division.$touch()"
            @blur="$v.division.$touch()"
          />
        </v-col>
        <v-col
          v-else
          cols="12"
          class="pa-0 pt-4"
        >
          <v-text-field
            id="designation-field"
            v-model.trim="designation"
            class="black--text"
            label="Official Designation"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('designation','Designation')"
            @input="$v.designation.$touch()"
            @blur="$v.designation.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="!isPic">
        <v-col
          v-if="$data.$meta.readOnly && !$data.$meta.isLoading"
          cols="6"
          class="pa-0"
        >
          <p class="label">
            Official Designation
          </p>
          <v-text-field
            v-model.trim="designation"
            class="black--text"
            solo
            flat
            placeholder="Designation"
            type="text"
            dense
            :outlined="!$data.$meta.readOnly"
            :disabled="$data.$meta.readOnly"
            :error-messages="genericError('designation','Designation')"
            @input="$v.designation.$touch()"
            @blur="$v.designation.$touch()"
          />
        </v-col>
        <v-col
          v-if="!$data.$meta.readOnly && !$data.$meta.isLoading"
          cols="12"
          class="pa-0 d-flex justify-end align-center"
        >
          <slot name="action" />
        </v-col>
      </v-row>
    </v-card>
    <v-dialog
      v-model="reasonModal.modal"
      width="35%"
      persistent
    >
      <v-card class="pa-4">
        <v-card-title
          class="pb-8"
          color="#162B54"
        >
          <b>{{ reasonModal.header }}</b>
        </v-card-title>
        <v-textarea
          v-if="isAdmin"
          id="reasonTextArea"
          v-model="reason"
          outlined
          class="pa-5"
          name="input-7-4"
          :label="reasonModal.label"
          :error-messages="genericError('reason', 'Reason')"
          @input="$v.reason.$touch()"
          @blur="$v.reason.$touch()"
        />
        <v-card-actions>
          <v-spacer />
          <template>
            <v-btn
              id="cancel-reason-button"
              color="primary"
              text
              @click="reasonModal.cancel"
            >
              Cancel
            </v-btn>
            <v-btn
              id="continue-button"
              class="pl-8 pr-8"
              color="primary"
              @click="reasonModal.continue"
            >
              Continue
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import users from '@/utils/webapi/users'
import adminProfileMixin from '@/mixins/adminProfileMixin'
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import { datesAreEqual, capitalize } from '@/utils/helpers.js'
import { privilegeMixin } from 'session-plugin'
// import users from '@/utils/webapi/users'
import { mapGetters } from 'vuex'
export default {
  components: {
  },
  mixins: [validationMixin, privilegeMixin, adminProfileMixin],
  props: {
    fields: { type: Object, default: undefined },
    operation: { type: String, default: undefined },
    editable: { type: Boolean, default: false },
    radius: { type: String, default: undefined },
    styles: { type: String, default: undefined }
  },
  data: function () {
    return this.fields
  },
  validations () {
    return { userRole: custom.role, ...custom.userProfile(this) }
  },
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    userRoleQuery () {
      return { _configType: { $nin: ['guest', 'default'] } }
    },
    userRoles () {
      return (this.$s && this.$s.userRole) || []
    },
    isAdmin () {
      return this.userRoles.includes('admin')
    },
    datesEqual () {
      if (datesAreEqual(this.fields.dateCreated, this.fields.dateUpdated)) {
        return true
      }
      return false
    },
    isActive: {
      get () {
        return this.status === 'Active'
      },
      set () {
        if (this.isActive) {
          this.status = 'Inactive'
        } else {
          this.status = 'Active'
        }
      }
    }
  },
  methods: {
    updateCrumb () {
      if (this.breadcrumbs === undefined) {
        this.breadcrumbs = {
          divider: '>',
          large: true,
          items: [
            {
              text: 'My Profile'
            },
            {
              text: 'General Information',
              to: {
                name: 'AdminAccount'
              }
            }
          ]
        }
      }
      this.breadcrumbs.items[1].text = `Edit Profile - ${this.profile.fullName}`
    },
    reasonDialog () {
      this.$v.reason.$reset()
      this.reason = ''
      const modal = this.reasonModal
      modal.header = this.isActive ? 'Enable User' : 'Disable User'
      modal.label = this.isActive ? 'Enable User' : 'Reason for Disabling'
      modal.cancel = () => {
        modal.modal = false
        this.isActive = !!this.isActive
        this.reason = 'reason'
      }
      modal.continue = () => {
        this.$v.reason.$touch()
        if (!this.$v.reason.$invalid) {
          this.statusDialog(this.userId, this.isActive ? 'enable' : 'disable')
        }
      }
      modal.modal = true
    },
    deleteDialog (userId) {
      this.$store.dispatch('showDialog', {
        title: 'Delete User',
        message: 'Are you sure you want to delete the selected user? This action cannot be undone.',
        buttonLabel: 'Delete',
        callback: () => { this.deleteUser(userId) }
      })
    },
    statusDialog (userId, action) {
      const buttonColor = action === 'enable' ? 'primary' : ''
      this.$store.dispatch('showDialog', {
        title: `${capitalize(action)} User`,
        message: `Are you sure you want to ${action} ${this.firstName} ${this.middleName} ${this.lastName}'s access on DBNMS?`,
        buttonLabel: 'Yes',
        buttonColor: buttonColor,
        callback: () => {
          this.switchUserStatus(userId, action)
          this.reasonModal.modal = false
          this.reason = action
          this.$v.reason.$reset()
        }
      })
    },
    async switchUserStatus (userId, action) {
      this.$v.reason.$touch()
      if (!this.$v.reason.$invalid) {
        let resp, label
        if (action === 'enable') {
          label = 'activated'
          resp = await users.activateUser(userId, { activationReason: this.reason })
        } else {
          label = 'deactivated'
          resp = await users.deactivateUser(userId, { deactivationReason: this.reason })
        }
        if (resp.ok) {
          this.$store.dispatch('showAlert', {
            message: `User successfully ${label}`,
            type: 'success'
          })
        } else {
          action === 'enable' ? this.isActive = false : this.isActive = true
          this.$store.dispatch('showAlert', {
            message: (await resp.json()).error,
            type: 'error'
          })
        }
      }
    },
    async deleteUser (userId) {
      this.$data.$meta.isLoading = true
      const resp = await users.deleteUser(userId)
      if (resp.ok) {
        this.$router.replace('/admin/users/admin')
        this.$store.dispatch('showAlert', {
          message: 'User successfully deleted',
          type: 'success'
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.$data.$meta.isLoading = false
    }
  }
}

</script>

<style scoped>
  .main__header
  {
    font-size: 24px;
  }
  .v-text-field
  {
    font-size: 20px;
    font-weight: bold;
  }
  .shadow{
     box-shadow:4px 8px 8px -2px #c3c3c3 !important;
  }
.radius{
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
  .formPadding{
    padding-left: 10rem;
    padding-right: 10rem;
  }
  .label{
    font-size: 14px;
    color: #bdbdbd;
    margin:0;
    font-weight:bolder;
    padding-left: 12px;
    padding-right: 12px
  }
.editBtn{
  padding: 1.35rem 1rem !important;
  font-size: small;
}
.v-input--is-disabled >>> fieldset {
  border:none !important;
}
.v-btn {
  text-transform:none !important;
}

</style>
